/**
 * Mark onboarding as ended when the user deliberately walks away.
 *
 * Called from the user-initiated project/space switch handlers (header
 * project switcher, browse tree, desktop compact dock) — NOT from the
 * project store itself, so programmatic space changes made by the
 * onboarding flow (demo scenes, bootstrap restore) can never self-abort
 * the flow. Persisting a terminal step here is what stops the intro from
 * auto-rerunning on every remount for users who left mid-flow
 * (2026-07-03 onboarding-rerun bug).
 *
 * Fire-and-forget: the switch itself must not wait on the backend PATCH.
 * `exitOnboardingFromAnyWebview` tears down the scene runtime and works
 * from owner and mirror webviews alike.
 */
import { isTauri } from "@/lib/is-tauri";
import { useOnboardingStore } from "@/stores/onboarding-store";
import { useProjectStore } from "@/stores/project-store";

export function abandonOnboardingOnUserSpaceSwitch(
  targetSpaceId: string,
): void {
  if (!isTauri()) return;
  const { step, stepHydrated } = useOnboardingStore.getState();
  // Not hydrated = we don't know the real step yet; null = never started;
  // terminal = nothing to abandon. Only a mid-flow step counts.
  if (!stepHydrated || step === null) return;
  if (step === "completed" || step === "skipped") return;
  // Re-selecting the current space (e.g. fit-on-click) is not a switch.
  if (useProjectStore.getState().currentSpace?.id === targetSpaceId) return;
  void import("./advance-step-bridge").then(
    ({ exitOnboardingFromAnyWebview }) =>
      exitOnboardingFromAnyWebview("skipped"),
  );
}
